/**
 * User Service
 * Handles user-related database operations
 */

import { COLLECTIONS, PERMISSIONS, ROLES, ROLE_PERMISSIONS, SCHEMAS } from '../config.js'
import { buildApiHeaders, resolveInternalApiUrl } from '@/utils/apiHeaders'

import { BaseService } from './BaseService.js'
import { createCondition } from '../interfaces/IDatabaseAdapter.js'

export class UserService extends BaseService {
  constructor() {
    super(COLLECTIONS.USERS)
  }

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user
   */
  async createUser(userData) {
    const validation = this.validateData(userData, SCHEMAS[COLLECTIONS.USERS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Set default role if not provided
    if (!userData.role) {
      userData.role = ROLES.USER
    }

    // Set default permissions based on role
    userData.permissions = ROLE_PERMISSIONS[userData.role] || []

    return await this.create(userData)
  }

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(userId, updateData) {
    const validation = this.validateData(updateData, SCHEMAS[COLLECTIONS.USERS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Update permissions if role changed
    if (updateData.role) {
      updateData.permissions = ROLE_PERMISSIONS[updateData.role] || []
    }

    return await this.update(userId, updateData)
  }

  /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<Object|null>} User or null if not found
   */
  async getByEmail(email) {
    const users = await this.findBy('email', email)
    return users.length > 0 ? users[0] : null
  }

  /**
   * Find user by email (alias for getByEmail)
   * @param {string} email - User email
   * @returns {Promise<Object|null>} User or null if not found
   */
  async findByEmail(email) {
    return this.getByEmail(email)
  }

  /**
   * Get users by role
   * @param {string} role - User role
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of users
   */
  async getByRole(role, options = {}) {
    return await this.findBy('role', role, options)
  }

  /**
   * Get users by team
   * @param {string} teamId - Team ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of users
   */
  async getByTeam(teamId, options = {}) {
    return await this.findBy('teams', teamId, options)
  }

  /**
   * Get multiple users by their IDs (optimized for batch operations)
   * Uses POST endpoint to handle large arrays of IDs efficiently
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<Array>} Array of users
   */
  async getByIds(userIds) {
    if (!userIds || userIds.length === 0) {
      return []
    }
    
    await this._ensureDatabaseService()
    
    // Use HTTP adapter's batch endpoint to handle large arrays
    if ((this as any).db.adapter && (this as any).db.adapter.baseUrl) {
      try {
        const url = resolveInternalApiUrl('/users/batch-fetch', (this as any).db.adapter.baseUrl)
        const response = await fetch(url, {
          method: 'POST',
          headers: buildApiHeaders({
            url,
            includeAuth: true,
          }),
          body: JSON.stringify({ userIds })
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch users: ${response.statusText}`)
        }
        
        return await response.json()
      } catch (error) {
        // Fallback to individual calls if batch fails
      }
    } else {
      // Try to get baseUrl from db service
      let baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'https://staging.services.leadconnectorhq.com/hltestmanager'
      if ((this as any).db && (this as any).db.apiBaseUrl) {
        baseUrl = (this as any).db.apiBaseUrl
      }
      
      try {
        const url = resolveInternalApiUrl('/users/batch-fetch', baseUrl)
        const response = await fetch(url, {
          method: 'POST',
          headers: buildApiHeaders({
            url,
            includeAuth: true,
          }),
          body: JSON.stringify({ userIds })
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch users: ${response.statusText}`)
        }
        
        return await response.json()
      } catch (error) {
        // Fallback to individual calls if direct call also fails
      }
    }
    
    // Fallback: use Promise.all for individual calls
    const users = await Promise.all(
      userIds.map(id => this.getById(id))
    )
    return users.filter(user => user !== null && user !== undefined)
  }

  /**
   * Add user to team
   * @param {string} userId - User ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Updated user
   */
  async addToTeam(userId, teamId) {
    const user = await this.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    if (!user.teams.includes(teamId)) {
      user.teams.push(teamId)
      return await this.update(userId, { teams: user.teams })
    }

    return user
  }

  /**
   * Remove user from team
   * @param {string} userId - User ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Updated user
   */
  async removeFromTeam(userId, teamId) {
    const user = await this.getById(userId)
    if (!user) {
      throw new Error('User not found')
    }

    user.teams = user.teams.filter(id => id !== teamId)
    return await this.update(userId, { teams: user.teams })
  }

  /**
   * Check if user has permission
   * @param {string} userId - User ID
   * @param {string} permission - Permission to check
   * @returns {Promise<boolean>} Whether user has permission
   */
  async hasPermission(userId, permission) {
    const user = await this.getById(userId)
    if (!user) {
      return false
    }

    return user.permissions.includes(permission)
  }

  /**
   * Get user permissions
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of permissions
   */
  async getUserPermissions(userId) {
    const user = await this.getById(userId)
    if (!user) {
      return []
    }

    return user.permissions
  }

  /**
   * Search users
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching users
   */
  async searchUsers(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['name', 'email'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get user statistics
   * @returns {Promise<Object>} User statistics
   */
  async getStatistics() {
    const users = await this.getAll()
    const roleCounts = {}
    const teamCounts = {}

    users.forEach(user => {
      // Count by role
      roleCounts[user.role] = (roleCounts[user.role] || 0) + 1

      // Count by team
      user.teams.forEach(teamId => {
        teamCounts[teamId] = (teamCounts[teamId] || 0) + 1
      })
    })

    return {
      total: users.length,
      roleCounts,
      teamCounts,
      lastUpdated: new Date()
    }
  }
}

export const userService = new UserService()
